import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, from, map, mergeMap, of, switchMap, tap, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { AppService } from './app.service';
import { CommonModalComponent } from './views/common-modal/common-modal.component';
import { ApiUrl } from '../assets/constants';
import { MatDialog } from '@angular/material/dialog';
import {PasswordDialogComponent} from './views/pages/password-dialog/password-dialog.component';

@Injectable()
export class interceptAjax implements HttpInterceptor {
  private isHandling401 = false; // Global flag to block further requests during 401 handling
  constructor(private cookieService: CookieService, private appService: AppService, private router: Router, private spinnerService: NgxSpinnerService) { }
  skipHeaders: any = ["login", "rundiffusion", "renew_refresh_token"];

  checkSkipHeader = (url: string, array: string[]) => array.some((substr: string) => url.includes(substr));

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let isSkipHeader = this.checkSkipHeader(req.url, this.skipHeaders);
    let forLogout = req.url.includes("auth/logout");
    let accessToken = this.cookieService.get("userData") ? JSON.parse(this.cookieService.get("userData")).access_token : null;

    if (!isSkipHeader && !forLogout && accessToken) {
      let modifiedReq = req.clone({ setHeaders: { 'Authorization': 'Bearer ' + accessToken } });
      return next.handle(modifiedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return this.handle401Error(req, next);
          }
          return throwError(error);
        })
      );
    }
    return next.handle(req);
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // if (this.isHandling401) {
    //   // Block all requests if already handling 401
    //   return throwError(() => new Error('Blocked request during 401 handling'));
    // }

    this.isHandling401 = true; // Set flag to block further requests
    let refreshToken = JSON.parse(this.cookieService.get('userData'));

    let refreshReq: any = {
      token: refreshToken.refresh_token,
      device_id: refreshToken.device_id,
    };

    return this.appService.postApi(ApiUrl.REFRESHTOKEN, refreshReq).pipe(
      switchMap((response: any) => {
        if (response.status === 401) {
          this.cookieService.deleteAll();
          this.router.navigate(['/login']);
          this.isHandling401 = false; // Reset flag
          return throwError(() => new Error('Refresh token expired or invalid'));
        }

        // Update token data
        refreshToken.access_token = response.access_token;
        refreshToken.access_expiration_time = response.access_expiration_time;
        refreshToken.refresh_token = response.refresh_token;
        refreshToken.refresh_expiration_time = response.refresh_expiration_time;

        document.cookie = `userData=${JSON.stringify(refreshToken)};expires=${new Date(
          refreshToken.refresh_expiration_time
        ).toUTCString()};path=/`;

        let newReq = req.clone({
          setHeaders: { Authorization: 'Bearer ' + refreshToken.access_token },
        });

        this.isHandling401 = false; // Reset flag after successful handling
        return next.handle(newReq);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Clear cookies and hide spinner
          this.cookieService.deleteAll();
          this.spinnerService.hide();

          // Open the modal and handle the user's response
          const modalRef = this.appService.open(CommonModalComponent, {
            size: 'ls',
            backdrop: 'static',
          });

          return from(modalRef.result).pipe(
            mergeMap((result: string) => {
              if (result) {
                // User selected "Yes" - retry token refresh
                return this.appService.postApi(ApiUrl.RENEWREFRESHTOKEN, refreshReq).pipe(
                  switchMap((newResponse: any) => {
                    refreshToken.access_token = newResponse.access_token;
                    refreshToken.access_expiration_time = newResponse.access_expiration_time;
                    refreshToken.refresh_token = newResponse.refresh_token;
                    refreshToken.refresh_expiration_time = newResponse.refresh_expiration_time;

                    document.cookie = `userData=${JSON.stringify(refreshToken)};expires=${new Date(
                      refreshToken.refresh_expiration_time
                    ).toUTCString()};path=/`;

                    let retryReq = req.clone({
                      setHeaders: { Authorization: 'Bearer ' + refreshToken.access_token },
                    });

                    this.isHandling401 = false; // Reset flag after successful handling
                    return next.handle(retryReq);
                  }),
                  catchError((retryError) => {
                    // If retry fails, redirect to landing page
                    this.cookieService.deleteAll();
                    this.router.navigate(['/login']);
                    this.isHandling401 = false; // Reset flag
                    return throwError(retryError);
                  })
                );
              } else {
                // User selected "No" - redirect to landing page
                this.router.navigate(['/login']);
                this.isHandling401 = false; // Reset flag
                return throwError(() => new Error('User canceled re-authentication'));
              }
            }),
            catchError(() => {
              // If modal is dismissed, redirect to landing page
              this.router.navigate(['/login']);
              this.isHandling401 = false; // Reset flag
              return throwError(() => new Error('User dismissed the modal'));
            })
          );
        }

        this.isHandling401 = false; // Reset flag for other errors
        return throwError(error);
      })
    );
  }





}

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {
  private navbarRestricted = true; // Set based on your condition

  constructor(private router: Router, private cookieService: CookieService,private dialog: MatDialog) {

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Call your common event trigger function here
        this.onRouteChange(event);
      }
    });

  }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    let isLocked = this.cookieService.get("lockenabled") === 'true';
    let checkUserData = this.cookieService.get("userData") && JSON.parse(this.cookieService.get("userData")) && JSON.parse(this.cookieService.get("userData")).access_token ? true : false;

      
    if (checkUserData && isLocked) {
      const dialogRef = this.dialog.open(PasswordDialogComponent, {
        width: '30%',
        disableClose: true, // Prevents dialog from being closed manually
      });
    
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'unlocked') {
          this.cookieService.set("lockenabled", "false");
        }
      });
    }
    let routeArray = ['/login', '/register', '/landing-page', '/forgetpassword', '/otp-verification']
    if (checkUserData && routeArray.includes(state.url)) { this.router.navigate(['/']); }
    if (!checkUserData) { this.router.navigate(['/landing-page']); }
    
    return true
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot):
    boolean | Observable<boolean> | Promise<boolean> {
    return this.canActivate(route, state);
  }

  onRouteChange(event: any) {
    let checkUserData = this.cookieService.get("userData") && JSON.parse(this.cookieService.get("userData")) && JSON.parse(this.cookieService.get("userData")).access_token ? true : false;
    let routeArray = ['/login', '/register', '/landing-page', '/forgetpassword', '/otp-verification']
    // if(checkUserData && routeArray.includes(event.url)){  this.router.navigate(['home']);}

  }
}
@Injectable()
export class UserExist implements CanActivate {
  constructor(private router: Router, private cookieService: CookieService) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    let checkUserData = this.cookieService.get("userData") && JSON.parse(this.cookieService.get("userData")) && JSON.parse(this.cookieService.get("userData")).access_token ? true : false;
    if (checkUserData) { this.router.navigate(['/home']); }
    return true
  }





}
@Injectable({
  providedIn: 'root'
})

export class FullAccess implements CanActivate {
  constructor(private router: Router, private cookieService: CookieService) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    let checkUserData = localStorage.getItem("isFullAccess")
    if (checkUserData == "0") {
      this.router.navigate(['/login'])
      return false;
    }
    return true
  }





}

@Injectable()
export class SubscriptionGuard implements CanActivate {
  constructor(private injector: Injector) {}

  private get http(): HttpClient { return this.injector.get(HttpClient); }
  private get router(): Router { return this.injector.get(Router); }

  canActivate(): Observable<boolean> {
    return this.checkAccess().pipe(
      tap(hasAccess => {
        if (!hasAccess) {
          this.router.navigate(['/billing']);
        }
      })
    );
  }

  private checkAccess(): Observable<boolean> {
    // Step 1: Check location subscription
    return this.http.get<any>(ApiUrl.PLANDETAILSFROMKEY).pipe(
      switchMap(res => {
        if (res?.result?.length) {
          return of(true);
        }
        return this.checkLicense();
      }),
      catchError(() => this.checkLicense())
    );
  }

  private checkLicense(): Observable<boolean> {
    // Step 2: Check user subscription/license
    return this.http.get<any>(ApiUrl.CHECKLICENSE).pipe(
      switchMap(res => {
        if (res?.result?.length) {
          return of(true);
        }
        return this.checkTrial();
      }),
      catchError(() => this.checkTrial())
    );
  }

  private checkTrial(): Observable<boolean> {
    // Step 3: Check trial status (Storage API — checks both admin trials and Login DB trials)
    return this.http.get<any>(`${ApiUrl.STORAGEAPI}/clinic/trial-status`).pipe(
      map(res => res?.is_active === true),
      catchError(() => of(false))
    );
  }
}
